import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Lock from '@nerdwallet/react-icon/gizmo/Lock';
import { CompareCardCheckbox } from '@nerdwallet/compare-credit-cards-bar';
import nds from '@nerdwallet/base-styles/classes';
import {
  Container as ABContainer,
  Variant,
  Control,
} from '@nerdwallet/nw-client-lib/ab';
import { Title, Box, Link, Text } from '@nerdwallet/currency';

import {
  APPLY_NOW_LINK_TYPES,
  LINK_TYPES,
  MarketplaceApplyNowImpression,
} from '@nerdwallet/react-marketplace-apply-now';

import triggerPebblePost from '../../lib/trigger-pebble-post';
import BulletedList from '../bulleted-list/bulleted-list';
import CallToApply from '../call-to-apply/call-to-apply';
import CreditCardImage from './credit-card-image/credit-card-image';
import EditorRating from '../editor-rating/editor-rating';
import ExpandingComponent from '../expanding-component/expanding-component';
import ExpandingRow from '../expanding-row/expanding-row';
import FeaturedBadge from '../featured-badge/featured-badge';
import Row from '../row/row';
import StatTable from './stat-table/stat-table';
import mapCardData from '../credit-card-table/map-card-data';

import { Experiment, isExperimentActive } from '../../lib/experiment-list';

import './credit-card-box.less';

class CreditCardBox extends React.PureComponent {
  render() {
    const {
      defaultOpen,
      drivers,
      experiments,
      featuredCard,
      hideProsAndCons,
      offer,
      productPosition,
      sectionPosition,
    } = this.props;

    const {
      bestFor,
      cardSlug,
      consBullets,
      editorialText,
      editorRating,
      id,
      image,
      issuer,
      logID,
      mappedDrivers,
      marketingBullets,
      payPerCall,
      prosBullets,
      termsAndFees,
      title,
    } = mapCardData(offer, drivers);

    const IS_MOBILE_EXP_ACTIVE = isExperimentActive(
      Experiment.MARKETPLACE_MOBILE_MOVE_CTA,
      experiments
    );

    const applyNow = (disableAnnotation, productLocation) => (
      <MarketplaceApplyNowImpression
        className="apply-now"
        disableAnnotation={disableAnnotation}
        id={`${id}-section-${sectionPosition}-position-${productPosition} cta`}
        productPosition={productPosition}
        sectionPosition={sectionPosition}
        productLocation={productLocation}
        onClick={triggerPebblePost}
        type={LINK_TYPES.BUTTON}
        linkType={APPLY_NOW_LINK_TYPES.applyNowButton}
        marketplaceEntity={offer}
      />
    );
    const callToApply = payPerCall ? (
      <CallToApply
        className={nds('width-100')}
        payPerCall={payPerCall}
        productId={id}
        productPosition={productPosition}
        sectionPosition={sectionPosition}
      />
    ) : null;
    const seeAdditionalDetails = (
      <MarketplaceApplyNowImpression
        id={`${id}-section-${sectionPosition}-position-${productPosition} card-details`}
        productPosition={productPosition}
        sectionPosition={sectionPosition}
        productLocation="card-see-details"
        onClick={triggerPebblePost}
        type={LINK_TYPES.LINK}
        linkType={APPLY_NOW_LINK_TYPES.applyLink}
        marketplaceEntity={offer}
      >
        <Box className="credit-card-box__main-container__marketing-bullets__see-additional-details">
          <Text component="h4">
            See additional details at {issuer}
            &apos;s website
          </Text>
        </Box>
      </MarketplaceApplyNowImpression>
    );
    // workaround for the fact that CompareCardCheckbox expects a finproservices card, not a marketplace card
    const wrapperForCardID = {
      id,
    };

    const ctaContainer = (
      <Row className={nds('padding-vertical-4', 'display-none--wide')}>
        {/* swap order of buttons for mobile cta experiment */}
        <ABContainer experimentID={Experiment.MARKETPLACE_MOBILE_MOVE_CTA.id}>
          <Control>
            <div>
              <Row
                className={classNames(
                  'credit-card-box__main-container__compare-checkbox-style',
                  {
                    [nds('background-neutral-lightest')]: !featuredCard,
                    [nds('background-blue-lightest')]: featuredCard,
                  }
                )}
              >
                <CompareCardCheckbox card={wrapperForCardID} />
              </Row>
              <Row
                className={classNames({
                  [nds('display-flex')]: !IS_MOBILE_EXP_ACTIVE, // retain these styles for Control, but turn off for Variant
                  [nds('flex-direction-row')]: !IS_MOBILE_EXP_ACTIVE,
                })}
              >
                {applyNow(true, 'card-main-button')}
                {callToApply}
              </Row>
            </div>
          </Control>
          <Variant
            variantName={Experiment.MARKETPLACE_MOBILE_MOVE_CTA.variant.ON}
          >
            <div>
              <Row
                className={classNames({
                  [nds('display-flex')]: !IS_MOBILE_EXP_ACTIVE, // retain these styles for Control, but turn off for Variant
                  [nds('flex-direction-row')]: !IS_MOBILE_EXP_ACTIVE,
                })}
              >
                {applyNow(true, 'card-main-button')}
                {callToApply}
              </Row>
              <Row className="credit-card-box__main-container__compare-checkbox-style">
                <CompareCardCheckbox card={wrapperForCardID} />
              </Row>
            </div>
          </Variant>
        </ABContainer>
        <span className="credit-card-box__main-container__cta-mobile-annotation">
          <Lock className="credit-card-box__main-container__cta-mobile-annotation__lock" />
          Securely with {issuer}
        </span>
      </Row>
    );

    const overview = (
      <div className="credit-card-box__main-container">
        <div
          className={classNames(
            [
              nds('display-flex'),
              nds('flex-direction-column'),
              nds('flex-direction-row--wide'),
            ],
            {
              [nds('align-items-center')]: IS_MOBILE_EXP_ACTIVE,
              [nds('align-items-flex-start--wide')]: IS_MOBILE_EXP_ACTIVE,
            }
          )}
        >
          <Row className="credit-card-box__main-container__image-button">
            <MarketplaceApplyNowImpression
              id={`${id}-section-${sectionPosition}-position-${productPosition} card-img`}
              productPosition={productPosition}
              sectionPosition={sectionPosition}
              productLocation="card-img"
              onClick={triggerPebblePost}
              type={LINK_TYPES.LINK}
              linkType={APPLY_NOW_LINK_TYPES.productImage}
              marketplaceEntity={offer}
            >
              <CreditCardImage
                className="credit-card-box__main-container__card-img"
                alt={`${title} card art`}
                src={image}
              />
            </MarketplaceApplyNowImpression>
            <Row className="credit-card-box__main-container__apply-buttons">
              {applyNow(false, 'card-main-button')}
              <CompareCardCheckbox card={wrapperForCardID} />
            </Row>
          </Row>
          <Row className={nds('width-100')}>
            <Row
              className={classNames(
                [nds('display-flex'), nds('flex-direction-row')],
                {
                  [nds('align-items-center')]: IS_MOBILE_EXP_ACTIVE,
                }
              )}
            >
              <Row
                className={classNames(nds('text-align-left--wide'), {
                  [nds('width-100')]: IS_MOBILE_EXP_ACTIVE,
                  [nds('text-align-center')]: IS_MOBILE_EXP_ACTIVE,
                })}
              >
                {featuredCard ? <FeaturedBadge label="Featured Card" /> : null}
                {bestFor ? (
                  <Box className="credit-card-box__main-container__editorial-font">
                    <Text size="small" bold>
                      {bestFor}
                    </Text>
                  </Box>
                ) : null}
                <Row className={nds('padding-bottom-3')}>
                  <MarketplaceApplyNowImpression
                    id={`${id}-section-${sectionPosition}-position-${productPosition} card-name`}
                    offerVersionId={logID}
                    productPosition={productPosition}
                    sectionPosition={sectionPosition}
                    onClick={triggerPebblePost}
                    type={LINK_TYPES.LINK}
                    linkType={APPLY_NOW_LINK_TYPES.offerName}
                    marketplaceEntity={offer}
                  >
                    <ABContainer
                      experimentID={Experiment.MARKETPLACE_MOBILE_MOVE_CTA.id}
                    >
                      <Control>
                        <Box className="credit-card-box__main-container__card-title">
                          <Text>{title}</Text>
                        </Box>
                      </Control>
                      <Variant
                        variantName={
                          Experiment.MARKETPLACE_MOBILE_MOVE_CTA.variant.ON
                        }
                      >
                        <Title
                          className="credit-card-box__main-container__card-title"
                          size="small"
                        >
                          {title}
                        </Title>
                      </Variant>
                    </ABContainer>
                  </MarketplaceApplyNowImpression>
                </Row>
                {editorialText ? (
                  <Box className="credit-card-box__main-container__editorial-font">
                    <Text size="small" bold>
                      What our experts say
                    </Text>
                  </Box>
                ) : null}
                <EditorRating
                  slug={cardSlug}
                  rating={editorRating}
                  copyStyle={nds('text-small')}
                />
                {IS_MOBILE_EXP_ACTIVE ? ctaContainer : null}
              </Row>
            </Row>
            <Row className="credit-card-box__main-container__marketing-bullets">
              <ExpandingComponent
                formName="card-marketing-bullets"
                fieldName="card-id"
                fieldValue={id}
                hideGradient={featuredCard}
                className="credit-card-box__main-container__marketing-bullets__desktop"
                defaultOpen={defaultOpen}
              >
                <Text>{editorialText}</Text>
                <BulletedList
                  bullets={marketingBullets}
                  copyStyle="text-small"
                />
                {seeAdditionalDetails}
              </ExpandingComponent>
              <Row className="credit-card-box__main-container__marketing-bullets__mobile-tablet">
                <ExpandingRow
                  formName="card-marketing-bullets"
                  fieldName="card-id"
                  fieldValue={id}
                  openText="Close"
                  closedText="See card details"
                  shouldScroll={false}
                  defaultOpen={defaultOpen}
                >
                  <div>
                    <Box className="credit-card-box__main-container__marketing-bullets__editorial-text">
                      <Text>{editorialText}</Text>
                    </Box>
                    <BulletedList
                      bullets={marketingBullets.slice(0, 2)}
                      copyStyle="text-small"
                    />
                  </div>
                  <div>
                    <BulletedList
                      bullets={marketingBullets.slice(2)}
                      copyStyle="text-small"
                    />
                    {seeAdditionalDetails}
                  </div>
                </ExpandingRow>
              </Row>
            </Row>
          </Row>
        </div>
        {IS_MOBILE_EXP_ACTIVE ? null : ctaContainer}
        <StatTable
          drivers={mappedDrivers}
          cardId={id}
          featuredCard={featuredCard}
        />
      </div>
    );
    const details = (
      <Row className="credit-card-box__pros-cons__expanded">
        <Row className="credit-card-box__pros-cons__expanded__details-box">
          <Title className="credit-card-box__pros-cons__expanded__details-box__title">
            Pros
          </Title>
          <BulletedList bullets={prosBullets} copyStyle="text-small" />
        </Row>
        <Row className="credit-card-box__pros-cons__expanded__details-box">
          <Title className="credit-card-box__pros-cons__expanded__details-box__title">
            Cons
          </Title>
          <BulletedList bullets={consBullets} copyStyle="text-small" />
        </Row>
        <Row className="credit-card-box__pros-cons__expanded__details-box">
          <Row className="credit-card-box__pros-cons__expanded__details-box__apply-now">
            {applyNow(false, 'card-pros-cons-button')}
          </Row>
        </Row>
      </Row>
    );

    const expandingRow = hideProsAndCons ? (
      <Fragment>{overview}</Fragment>
    ) : (
      <ExpandingRow
        formName="card-pros-cons"
        fieldName="card-id"
        fieldValue={id}
        openText="Close"
        closedText="See NerdWallet's pros &amp; cons"
        drawerElementClassName="credit-card-box__pros-cons"
        defaultOpen={defaultOpen}
      >
        {overview}
        {details}
      </ExpandingRow>
    );

    return (
      <Row
        className={classNames('credit-card-box', {
          'mobile-experiment': IS_MOBILE_EXP_ACTIVE,
        })}
        key={`credit-card-box-${id}`}
      >
        <div className="credit-card-box__wrapper">
          <Row
            className="credit-card-box__border"
            backgroundColor={featuredCard ? 'blue-lightest' : ''}
          >
            {expandingRow}
          </Row>
          {termsAndFees ? (
            <Link href={termsAndFees}>
              <Box className="credit-card-box__rates-and-fees">
                <Text size="small">VIEW RATES AND FEES</Text>
              </Box>
            </Link>
          ) : null}
        </div>
      </Row>
    );
  }
}

// eslint-disable-next-line react/static-property-placement -- directive added automatically by Shepherd migration
CreditCardBox.propTypes = {
  offer: PropTypes.object,
  drivers: PropTypes.arrayOf(
    PropTypes.shape({
      titleText: PropTypes.string,
      item: PropTypes.string,
      subItem: PropTypes.string,
    })
  ),
  experiments: PropTypes.object,
  featuredCard: PropTypes.bool,
  hideProsAndCons: PropTypes.bool,
  defaultOpen: PropTypes.bool,
  productPosition: PropTypes.number,
  sectionPosition: PropTypes.number,
};

// eslint-disable-next-line react/static-property-placement -- directive added automatically by Shepherd migration
CreditCardBox.defaultProps = {
  offer: {},
  drivers: [],
  experiments: {},
  hideProsAndCons: false,
  featuredCard: false,
  productPosition: 0,
  sectionPosition: 0,
};

export default CreditCardBox;
